import React, { useRef, useState } from 'react';
import * as yup from 'yup';
import { SxStyle } from '../../../../types';
import { useStringsContext } from '../../../../contexts/strings';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextInputWithValidate, {
  InputWithValidateProps,
} from '../../../common/input/text-input-with-validate';
import Popper from '@mui/material/Popper';
import Card from '@mui/material/Card';
import Grid2 from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import { bottomBarIconsMap, bottomBarIconsMapCustom } from '../icons';
import { AppConfigBottomBarItemKey, EditableBottomBarItem, MenuItemEditableKeys } from '../types';
import { AppMenuItemBoxProps } from './app-menu-item';
import svStrings from '../../../../sv-strings';
import { ValidatedValue } from '../../../../contexts/formWithValidation';
import SelectWithValidate from '../../../common/input/select-with-validate';
import { DefaultBottomBarItemKey, defaultStandardItems } from '../items';
import RadioWithValidate from '../../../common/input/radio-with-validate';

const styles: SxStyle = {
  editContainer: {
    padding: 2,
    borderRadius: 1,
    backgroundColor: '#F5F5F5',

    '& .MuiTextField-root': { backgroundColor: 'transparent' },
    '& .MuiOutlinedInput-root': { backgroundColor: 'white' },
  },
  selectButton: {
    padding: 0,
    minWidth: '40px',
    height: '40px',
    backgroundColor: 'white',
  },
  textInput: {
    backgroundColor: 'white',
  },
  buttons: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  popperContainer: {
    padding: 2,
    marginTop: 1,
    width: '310px',
    display: 'grid',
  },
  iconButtons: {
    padding: 1,
  },
  iconButton: {
    borderRadius: 1,
    backgroundColor: '#90CAF933',
    width: '38px',
    height: '38px',
  },
};

const menuItemTypeOptions = ['url', 'app'] as const;
type MenuItemTypeOption = (typeof menuItemTypeOptions)[number];
const menuItemTypeOptionsText = [
  svStrings.pushFields.destinationOptionWebUrl,
  svStrings.pushFields.destinationOptionApp,
];

const textValidation = yup.string().required(svStrings.validations.isRequired);
const urlValidation = yup
  .string()
  .url(svStrings.validations.validUrl)
  .required(svStrings.validations.isRequired);

const menuItemLinkOptions = (Object.keys(defaultStandardItems) as DefaultBottomBarItemKey[]).filter(
  (item) => item !== 'customUrl'
);

const menuItemLinkOptionsText = menuItemLinkOptions.map((link) => defaultStandardItems[link].text);

interface Props
  extends Pick<AppMenuItemBoxProps, 'onDelete' | 'onSave'>,
    Pick<InputWithValidateProps, 'onValidation'> {
  bottomBarItem: EditableBottomBarItem;
  onValue?: (key: MenuItemEditableKeys, value: any | null) => void;
}

const AppMenuItemEdit = ({ bottomBarItem, onDelete, onValue, onValidation }: Props) => {
  const { strings } = useStringsContext();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const validatedValues = useRef<ValidatedValue>({});
  const [selectedItemLink, setSelectedItemLink] = useState<AppConfigBottomBarItemKey>(
    bottomBarItem.type
  );
  const [selectedItemType, setSelectedItemType] = useState<MenuItemTypeOption>(
    bottomBarItem.type === 'customUrl' ? 'url' : 'app'
  );

  const onShowPopper = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(anchorEl ? null : event.currentTarget);
  const openPopper = Boolean(anchorEl);
  const id = openPopper ? 'simple-popper' : undefined;

  const onSelectIcon = (selectedIcon: string) => {
    onValue?.('icon', selectedIcon);
    setAnchorEl(null);
  };

  const onTypeValue = (key: string, value: MenuItemTypeOption) => {
    delete validatedValues.current['text'];
    delete validatedValues.current['icon'];
    delete validatedValues.current['type'];
    delete validatedValues.current['url'];

    // Select default item when switching from customUrl
    const itemLink =
      value === 'app' && selectedItemLink === 'customUrl'
        ? menuItemLinkOptions[0]
        : selectedItemLink;

    const itemType = value === 'url' ? 'customUrl' : itemLink;

    const { text, icon, type } = defaultStandardItems[itemType];

    onValue?.('text', text);
    onValue?.('icon', icon);
    onValue?.('type', type);

    setSelectedItemLink(itemLink);
    setSelectedItemType(value);
  };

  const onTextValue = (key: string, value: string) => {
    onValue?.('text', value);
  };

  const onUrlValue = (key: string, value: string) => {
    onValue?.('url', value);
  };

  const onLinkValue = (key: string, value: DefaultBottomBarItemKey) => {
    const { text, icon, type } = defaultStandardItems[value];

    onValue?.('text', text);
    onValue?.('icon', icon);
    onValue?.('type', type);

    setSelectedItemLink(value);
  };

  const handleDelete = () => {
    onDelete?.(bottomBarItem);
  };

  const handleValidation = (key: string, isValid: boolean) => {
    validatedValues.current[key] = isValid;
    const allIsValid = Object.values(validatedValues.current).every((valid) => valid);
    onValidation?.(String(bottomBarItem.id), allIsValid);
  };

  const MenuItemIconComponent = bottomBarIconsMap[bottomBarItem.icon]
    ? bottomBarIconsMap[bottomBarItem.icon]
    : null;

  return (
    <>
      <Stack sx={styles.editContainer} spacing={2}>
        <RadioWithValidate
          name="type"
          options={[...menuItemTypeOptions]}
          optionsText={menuItemTypeOptionsText}
          value={selectedItemType}
          onValue={(key, value) => onTypeValue(key, value)}
        />
        {selectedItemType === 'app' && (
          <Stack spacing={1}>
            <Typography variant="body1-bold">{strings.appMenu.menuItemSelection}</Typography>
            <SelectWithValidate
              name="link"
              size="small"
              options={menuItemLinkOptions}
              optionsText={menuItemLinkOptionsText}
              value={selectedItemLink || ''}
              onValue={(key, value) => onLinkValue(key, value)}
              validation={textValidation}
              onValidation={handleValidation}
            />
          </Stack>
        )}
        {selectedItemType === 'url' && (
          <Stack spacing={1}>
            <Typography variant="body1-bold">{strings.appMenu.menuItemTitle}</Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" sx={styles.selectButton} onClick={onShowPopper}>
                {MenuItemIconComponent && <MenuItemIconComponent fill="black" />}
              </Button>
              <TextInputWithValidate
                sx={styles.textInput}
                name="text"
                size="small"
                value={bottomBarItem.text}
                onValue={onTextValue}
                validation={textValidation}
                onValidation={handleValidation}
                placeholder={strings.appMenu.menuItemPlaceholderName}
              />
            </Stack>
          </Stack>
        )}
        {selectedItemType === 'url' && (
          <Stack spacing={1}>
            <Typography variant="body1-bold">{strings.appMenu.menuItemLink}</Typography>
            <TextInputWithValidate
              sx={styles.textInput}
              name="url"
              size="small"
              value={bottomBarItem.url}
              onValue={onUrlValue}
              validation={urlValidation}
              onValidation={handleValidation}
              placeholder={strings.appMenu.menuItemPlaceholderUrl}
            />
          </Stack>
        )}
        <Box sx={styles.buttons}>
          <Button variant="contained" color="error" onClick={handleDelete}>
            {strings.appMenu.menuItemRemove}
          </Button>
        </Box>
      </Stack>
      <Popper id={id} open={openPopper} anchorEl={anchorEl} placement="bottom-start">
        <Card sx={styles.popperContainer} elevation={3}>
          <Grid2 container>
            {Object.entries(bottomBarIconsMapCustom).map(([name, IconComponent], index) => (
              <Grid2 key={index} sx={styles.iconButtons}>
                <IconButton
                  disabled={name === bottomBarItem.icon}
                  disableTouchRipple
                  sx={{
                    ...styles.iconButton,
                    border: name === bottomBarItem.icon ? '2px solid #003168' : 'none',
                  }}
                  onClick={() => onSelectIcon(name)}
                >
                  <IconComponent fill="black" />
                </IconButton>
              </Grid2>
            ))}
          </Grid2>
        </Card>
      </Popper>
    </>
  );
};

export default AppMenuItemEdit;
